import React, { useState } from "react";
import { Card, Row, Col, Badge, Button, Modal, Form, Container } from "react-bootstrap";
import toast from "react-hot-toast";
import moment from "moment";
import { updateTenantFn } from "../../../Utility/ApiService";
import { formatPhoneNumber } from "../../../Utility/index";
import { SpinnerLoading } from "../../../components/Loadings";

export default function SMSNotificationSettings({ user, setUser }) {
  const [showEnableModal, setShowEnableModal] = useState(false);
  const [smsConsent, setSmsConsent] = useState(false);
  const [termsConsent, setTermsConsent] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [loading, setLoading] = useState(false);

  const isOptedIn = user?.sms_opt_in === 1;

  const handleOpenEnableModal = () => {
    setPhoneNumber(user?.phone || "");
    setSmsConsent(false);
    setShowEnableModal(true);
  };

  const handleCloseEnableModal = () => {
    setShowEnableModal(false);
    setSmsConsent(false);
    setTermsConsent(false);
    setPhoneNumber("");
  };

  const handleEnableSMS = async () => {
    if (!smsConsent) return;
    if (!phoneNumber || phoneNumber.length < 14) {
      toast.error("Please provide a valid phone number");
      return;
    }

    setLoading(true);
    try {
      const updateObj = {
        id: user.id,
        phone: phoneNumber,
        sms_opt_in: 1,
        sms_opt_in_date: moment().format("YYYY-MM-DD HH:mm:ss"),
        sms_opt_out_date: null,
        last_modified: moment().format("YYYY-MM-DD HH:mm:ss"),
      };

      const res = await updateTenantFn(updateObj);
      setUser(res);
      toast.success("SMS notifications enabled");
      handleCloseEnableModal();
    } catch (error) {
      console.log("Enable SMS error:", error);
      toast.error("Failed to enable SMS notifications");
    } finally {
      setLoading(false);
    }
  };

  const handleDisableSMS = async () => {
    setLoading(true);
    try {
      const updateObj = {
        id: user.id,
        sms_opt_in: 0,
        sms_opt_out_date: moment().format("YYYY-MM-DD HH:mm:ss"),
        last_modified: moment().format("YYYY-MM-DD HH:mm:ss"),
      };

      const res = await updateTenantFn(updateObj);
      setUser(res);
      toast.success("SMS notifications disabled");
    } catch (error) {
      console.log("Disable SMS error:", error);
      toast.error("Failed to disable SMS notifications");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <div className="loadingSpinner">
          <SpinnerLoading />
        </div>
      )}

      <Row>
        <Col md="6" xl="6">
          <h4 className="d-flex align-items-center mt-4">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="me-2">
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M7.5 2C5.01472 2 3 4.01472 3 6.5V17.5C3 19.9853 5.01472 22 7.5 22H16.5C18.9853 22 21 19.9853 21 17.5V6.5C21 4.01472 18.9853 2 16.5 2H7.5ZM5 6.5C5 5.11929 6.11929 4 7.5 4H16.5C17.8807 4 19 5.11929 19 6.5V17.5C19 18.8807 17.8807 20 16.5 20H7.5C6.11929 20 5 18.8807 5 17.5V6.5ZM8 7C7.44772 7 7 7.44772 7 8C7 8.55228 7.44772 9 8 9H16C16.5523 9 17 8.55228 17 8C17 7.44772 16.5523 7 16 7H8ZM7 12C7 11.4477 7.44772 11 8 11H16C16.5523 11 17 11.4477 17 12C17 12.5523 16.5523 13 16 13H8C7.44772 13 7 12.5523 7 12ZM8 15C7.44772 15 7 15.4477 7 16C7 16.5523 7.44772 17 8 17H12C12.5523 17 13 16.5523 13 16C13 15.4477 12.5523 15 12 15H8Z"
                fill="#06122B"
              />
            </svg>
            SMS Messaging
          </h4>

          <Card className="mb-3 sms-card">
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  <small className="text-muted">
                    Receive text messages for rent reminders, payment confirmations, and property alerts.
                  </small>
                </div>
                <Badge
                  bg={isOptedIn ? "success" : "secondary"}
                  className={`ms-3${isOptedIn ? " sms-badge-active" : ""}`}
                >
                  {isOptedIn ? "Active" : "Disabled"}
                </Badge>
              </div>

              {isOptedIn && user?.phone && (
                <div className="mt-2">
                  <small className="text-muted">
                    Phone: {formatPhoneNumber(user.phone)}
                  </small>
                </div>
              )}

              <div className="mt-3">
                {isOptedIn ? (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={handleDisableSMS}
                    disabled={loading}
                  >
                    {loading ? "Disabling..." : "Disable SMS"}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleOpenEnableModal}
                    disabled={loading}
                  >
                    Enable SMS
                  </Button>
                )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enable SMS Modal */}
      <Modal
        show={showEnableModal}
        onHide={handleCloseEnableModal}
        centered
        className="modal-style-1 w-100 pd-md"
      >
        <Modal.Header>
          <Modal.Title as="h3" className="w-100 text-center">
            Enable SMS Messaging
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="mb-3">
          <Row>
            <Col>
              <Form.Group className="mb-3">
                <Form.Label>Phone Number</Form.Label>
                <Form.Control
                  type="text"
                  maxLength={14}
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(formatPhoneNumber(e.target.value))}
                  placeholder="(555) 555-5555"
                />
                {phoneNumber && phoneNumber.length < 14 && (
                  <Form.Text className="text-danger">
                    Please provide a valid phone number
                  </Form.Text>
                )}
              </Form.Group>

              <Form.Check
                type="checkbox"
                id="sms-consent-checkbox"
                className="mb-3"
                label={
                  <span className="text-muted" style={{ fontSize: "14px" }}>
                    I consent to receive transactional SMS notifications from Arrivo regarding
                    my rental account, including rent reminders, payment confirmations,
                    maintenance updates, and property alerts. You may receive 2-15 messages
                    per month depending on account activity. Message and data rates may apply.
                    Reply STOP to opt-out or HELP for assistance.
                  </span>
                }
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
              />

              <Form.Check
                type="checkbox"
                id="sms-terms-checkbox"
                className="mb-3"
                label={
                  <span className="text-muted" style={{ fontSize: "14px" }}>
                    I agree to the{" "}
                    <a href="https://arrivo.ai/terms-and-conditions/" target="_blank" rel="noopener noreferrer">
                      Terms and Conditions
                    </a>{" "}
                    and{" "}
                    <a href="https://arrivo.ai/privacy" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  </span>
                }
                checked={termsConsent}
                onChange={(e) => setTermsConsent(e.target.checked)}
              />
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer>
          <Container className="m-0">
            <Row>
              <Col xs={6}>
                <Button className="btn-reset w-100" onClick={handleCloseEnableModal}>
                  Cancel
                </Button>
              </Col>
              <Col xs={6}>
                <Button
                  className="w-100"
                  disabled={!smsConsent || !termsConsent || !phoneNumber || phoneNumber.length < 14 || loading}
                  onClick={handleEnableSMS}
                >
                  {loading ? "Enabling..." : "Enable"}
                </Button>
              </Col>
            </Row>
          </Container>
        </Modal.Footer>
      </Modal>
    </>
  );
}
